import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { createClient } from '@supabase/supabase-js';
import { Button } from './ui/button';
import { Eye, EyeOff, Building2, User, Mail, AlertCircle, Languages, Smartphone } from 'lucide-react';
import { useI18n } from '../contexts/LanguageContext';
import { LanguagePicker } from './LanguagePicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { setTenantId as saveTenantId, sanitizeTenantId } from '../lib/tenant-utils';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import type { Language } from '../lib/i18n';
import { GoogleSignInButton, decodeGoogleCredential } from './GoogleSignInButton';
import { tx } from '../lib/tx';

const BUSINESS_TYPE_IDS = ['dryCleaning', 'carpetWashing', 'sofaWashing', 'carWash', 'shoeShine', 'tailoring', 'barber'] as const;
const BUSINESS_TYPE_ICONS: Record<string, string> = {
  dryCleaning:   '🧺',
  carpetWashing: '🪣',
  shoeShine:     '👟',
  sofaWashing:   '🛋️',
  carWash:       '🚗',
  tailoring:     '✂️',
  barber:        '💈',
};

// Firma kodu (tenant id) transliterasyonu tek kaynaktan: sanitizeTenantId (tenant-utils).
// Kiril/Türkçe/aksanlı girişi Latin'e çevirir (химчистка -> himchistka); login ile birebir aynı.

interface SignupFormData {
  companyCode: string;
  companyName: string;
  ownerName: string;
  ownerEmail: string;
  phone: string;
  username: string;
  password: string;
  confirmPassword: string;
  plan: 'starter';
  country: string;
}

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-df8bdc4f`;

export const COUNTRIES = [
  { code: 'TR', name: 'Türkiye', flag: '🇹🇷', currency: 'TRY', languages: ['tr'], phonePrefix: '+90' },
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD', languages: ['en'], phonePrefix: '+1' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', languages: ['en'], phonePrefix: '+44' },
  { code: 'DE', name: 'Deutschland', flag: '🇩🇪', currency: 'EUR', languages: ['de'], phonePrefix: '+49' },
  { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR', languages: ['fr'], phonePrefix: '+33' },
  { code: 'ES', name: 'España', flag: '🇪🇸', currency: 'EUR', languages: ['es'], phonePrefix: '+34' },
  { code: 'IT', name: 'Italia', flag: '🇮🇹', currency: 'EUR', languages: ['it'], phonePrefix: '+39' },
  { code: 'CN', name: '中国', flag: '🇨🇳', currency: 'CNY', languages: ['zh'], phonePrefix: '+86' },
  { code: 'JP', name: '日本', flag: '🇯🇵', currency: 'JPY', languages: ['ja'], phonePrefix: '+81' },
  { code: 'KR', name: '대한민국', flag: '🇰🇷', currency: 'KRW', languages: ['ko'], phonePrefix: '+82' },
  { code: 'IN', name: 'भारत', flag: '🇮🇳', currency: 'INR', languages: ['hi'], phonePrefix: '+91' },
  { code: 'BR', name: 'Brasil', flag: '🇧🇷', currency: 'BRL', languages: ['pt'], phonePrefix: '+55' },
  { code: 'RU', name: 'Россия', flag: '🇷🇺', currency: 'RUB', languages: ['ru'], phonePrefix: '+7' },
];

function detectBrowserLanguage(): string {
  try {
    const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || '';
    if (timezone.includes('Istanbul') || timezone.includes('Ankara')) return 'tr';
    if (timezone.startsWith('Europe/')) return 'en';
    if (timezone.startsWith('Asia/')) return 'en';
    if (timezone.startsWith('America/')) return 'en';
    return 'tr';
  } catch {
    return 'tr';
  }
}

function detectBrowserCountry(): string {
  try {
    const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || '';
    if (timezone.includes('Istanbul') || timezone.includes('Ankara')) return 'TR';
  } catch {}
  return detectBrowserLanguage();
}

function SignupPageComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language: currentLanguage, setLanguage } = useI18n();
  
  const [step, setStep] = useState<'business-type' | 'details' | 'verify-email'>('business-type');
  const [businessType, setBusinessType] = useState('dryCleaning');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [error, setError] = useState('');
  const [referredBy, setReferredBy] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const detectedCountry = typeof window !== 'undefined' ? detectBrowserCountry() : 'TR';
  
  const [formData, setFormData] = useState<SignupFormData>({
    companyCode: '',
    companyName: '',
    ownerName: '',
    ownerEmail: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
    plan: 'starter',
    country: detectedCountry,
  });

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) setReferredBy(refCode);
  }, [searchParams]);

  // Davet linki üzerinden gelinmişse firma bilgilerini önceden doldur
  useEffect(() => {
    const raw = localStorage.getItem('pendingInvitePrefill');
    if (!raw) return;
    try {
      const prefill = JSON.parse(raw) as { companyName?: string; phone?: string; ownerEmail?: string };
      setFormData(prev => ({
        ...prev,
        ...(prefill.companyName ? { companyName: prefill.companyName } : {}),
        ...(prefill.phone       ? { phone: prefill.phone }             : {}),
        ...(prefill.ownerEmail  ? { ownerEmail: prefill.ownerEmail }   : {}),
      }));
    } catch { /* geçersiz JSON — yoksay */ }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const sendVerificationCode = async () => {
    setIsLoading(true);
    setLoadingMsg('📧 Email gönderiliyor...');
    setError('');

    try {
      const response = await fetch(`${API_BASE}/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          email: formData.ownerEmail.trim().toLowerCase(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Doğrulama kodu gönderilemedi');
      }

      setVerificationSent(true);
      setStep('verify-email');
      setCountdown(600);
      
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err: any) {
      setError(err.message || 'Doğrulama kodu gönderilemedi');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          email: formData.ownerEmail.trim().toLowerCase(),
          code: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Doğrulama başarısız');
      }

      await handleSignup();
    } catch (err: any) {
      setError(err.message || 'Doğrulama başarısız');
    } finally {
      setIsLoading(false);
    }
  };

  const validateDetailsForm = () => {
    if (!formData.companyCode.trim()) {
      setError(t('signup.errors.companyCodeRequired'));
      return false;
    }
    // Kiril/aksanlı girişte Latin'e çevrilmiş kod en az 3 karakter olmalı
    if (sanitizeTenantId(formData.companyCode).length < 3) {
      setError(t('signup.errors.companyCodeTooShort'));
      return false;
    }
    if (!formData.companyName.trim()) {
      setError(t('signup.errors.companyNameRequired'));
      return false;
    }
    if (!formData.ownerName.trim()) {
      setError(t('signup.errors.ownerNameRequired'));
      return false;
    }
    if (!formData.ownerEmail.trim()) {
      setError(t('signup.errors.emailRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
      setError(t('signup.errors.emailInvalid'));
      return false;
    }
    return true;
  };

  // Google ile kayıt: ad soyad ve e-posta Google hesabından doldurulur (firma bilgileri yine girilir)
  const [googleFilled, setGoogleFilled] = useState(false);
  const handleGoogleFill = (credential: string) => {
    const info = decodeGoogleCredential(credential);
    if (!info.email) return;
    setFormData(prev => ({
      ...prev,
      ownerEmail: String(info.email).toLowerCase(),
      ownerName: prev.ownerName || info.name || '',
    }));
    setGoogleFilled(true);
    setError('');
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateDetailsForm()) {
      return;
    }

    await handleSignup();
  };

  const handleSignup = async () => {
    setIsLoading(true);
    setLoadingMsg('🏗️ Hesap oluşturuluyor...');
    setError('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(`${API_BASE}/signup`, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          tenantId: sanitizeTenantId(formData.companyCode),
          companyName: formData.companyName.trim(),
          name: formData.ownerName.trim(),
          email: formData.ownerEmail.trim().toLowerCase(),
          phone: formData.phone.trim(),
          password: 'auto-generated',
          country: formData.country,
          language: currentLanguage,
          serviceType: businessType,
          referralCode: referredBy,
          fromInvite: !!localStorage.getItem('pendingInviteCode'),
          signupSource: sessionStorage.getItem('utm_source') || 'organic',
          signupMedium: sessionStorage.getItem('utm_medium') || '',
          signupCampaign: sessionStorage.getItem('utm_campaign') || '',
        }),
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('signup.errors.signupFailed'));
      }

      saveTenantId(sanitizeTenantId(formData.companyCode));
      localStorage.setItem('appLanguage', currentLanguage);
      localStorage.setItem('newTenantCreated', 'true');

      // Meta Pixel — kayıt tamamlandı
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'CompleteRegistration');
      }
      localStorage.setItem('pendingDefaultService', businessType);
      localStorage.removeItem('user');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_name');
      localStorage.removeItem('user_role');
      localStorage.removeItem('access_token');

      // Default credentials
      const credentials = {
        companyCode: sanitizeTenantId(formData.companyCode),
        patronUsername: 'patron',
        adminPin: '1234',
        cashierPin: '0000'
      };

      // Davet kodu varsa login sayfasına taşı
      const pendingInviteCode = localStorage.getItem('pendingInviteCode') || undefined;
      navigate('/login', {
        state: {
          message: data.referral?.message || t('signup.success'),
          tenantId: sanitizeTenantId(formData.companyCode),
          referralApplied: data.referral?.applied || false,
          credentials: data.credentials || credentials,
          inviteCode: pendingInviteCode,
        }
      });
   
    } catch (err: any) {
      clearTimeout(timeoutId);
      console.error('Signup error:', err);
      if (err.name === 'AbortError') {
        setError('Bağlantı zaman aşımına uğradı. Lütfen tekrar deneyin.');
      } else {
        setError(err.message || t('signup.errors.signupFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectedCountryData = COUNTRIES.find(c => c.code === formData.country);
  const phonePlaceholder = selectedCountryData 
    ? `${selectedCountryData.phonePrefix} ${formData.country === 'TR' ? '555 123 4567' : '123 456 7890'}`
    : t('signup.placeholders.phone');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="fixed top-4 right-4 z-10">
        <LanguagePicker />
      </div>

      <div className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-center">
          <img src="/logo.jpg" alt="Cleandy" className="h-10 object-contain" />
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {step === 'business-type' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <img src="/logo.jpg" alt="Cleandy" className="h-16 mx-auto object-contain mb-6" />
                <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('signup.businessType.question' as any)}</h1>
                <p className="text-gray-500 text-sm">{t('signup.businessType.hint' as any)}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                {BUSINESS_TYPE_IDS.map(id => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setBusinessType(id)}
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
                      businessType === id
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-5xl">{BUSINESS_TYPE_ICONS[id]}</span>
                    <div className="text-center">
                      <p className={`font-semibold text-base ${businessType === id ? 'text-blue-700' : 'text-gray-800'}`}>
                        {t(`service.${id}` as any)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{t(`signup.bt.${id}.desc` as any)}</p>
                    </div>
                    {businessType === id && (
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                        <svg viewBox="0 0 20 20" fill="white" className="w-4 h-4"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd"/></svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setStep('details')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold text-base transition-colors"
              >
                {t('signup.businessType.continue' as any)} →
              </button>
            </div>
          </div>
        )}

        {step === 'details' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <img src="/logo.jpg" alt="Cleandy" className="h-16 mx-auto object-contain mb-6" />
                <h1 className="text-3xl font-bold text-gray-800 mb-2">{t('signup.companyDetails')}</h1>
                <p className="text-gray-600">{t('signup.subtitle')}</p>
                {!referredBy && (
                  <p className="mt-3 inline-block rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-sm text-green-800">
                    {tx(currentLanguage, '15 gün tüm özellikler ücretsiz. Sonra dilerseniz ücretsiz pakette devam edersiniz; kart bilgisi gerekmez.', '15 days with all features free. After that you can stay on the free package if you like; no card required.')}
                  </p>
                )}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Google ile kayıt — ad ve e-posta Google hesabından gelir */}
              <div className="mb-6">
                {googleFilled ? (
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800 text-center">
                    ✓ Google hesabınızdan dolduruldu: <b>{formData.ownerEmail}</b>
                  </div>
                ) : (
                  <>
                    <GoogleSignInButton onCredential={handleGoogleFill} language={currentLanguage} text="signup_with" />
                    <div className="flex items-center gap-3 mt-4 text-xs text-gray-400">
                      <span className="flex-1 h-px bg-gray-200" />veya bilgileri elle girin<span className="flex-1 h-px bg-gray-200" />
                    </div>
                  </>
                )}
              </div>

              <form onSubmit={handleDetailsSubmit} className="space-y-6">
                {referredBy && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">🎁</span>
                      <div>
                        <p className="font-semibold text-green-800">
                          {t('signup.referral.title') || 'Arkadaşın tarafından davet edildin!'}
                        </p>
                        <p className="text-sm text-green-600">
                          {t('signup.referral.bonus') || '15 gün ücretsiz deneyeceksin!'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="inline h-4 w-4 mr-1" />
                    {t('signup.fields.companyCode')} *
                  </label>
                  <input type="text" name="companyCode" value={formData.companyCode} onChange={handleInputChange}
                    placeholder={t('signup.placeholders.companyCode')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                  {formData.companyCode && sanitizeTenantId(formData.companyCode) !== formData.companyCode.trim().toLowerCase() && (
                    <p className="mt-1 text-xs text-blue-600">
                      {currentLanguage === 'ru' ? 'Код входа:' : currentLanguage === 'en' ? 'Login code:' : 'Giriş kodu:'} <b>{sanitizeTenantId(formData.companyCode)}</b>
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">{t('signup.hints.companyCode')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('signup.fields.companyName')} *
                  </label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange}
                    placeholder={t('signup.placeholders.companyName')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="inline h-4 w-4 mr-1" />
                    {t('signup.fields.ownerName')} *
                  </label>
                  <input type="text" name="ownerName" value={formData.ownerName} onChange={handleInputChange}
                    placeholder={t('signup.placeholders.ownerName')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="inline h-4 w-4 mr-1" />
                    {t('signup.fields.email')} *
                  </label>
                  <input type="email" name="ownerEmail" value={formData.ownerEmail} onChange={handleInputChange}
                    placeholder={t('signup.placeholders.email')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Smartphone className="inline h-4 w-4 mr-1" />
                    {t('signup.fields.phone')} *
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                    placeholder={phonePlaceholder}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('signup.fields.country')}
                  </label>
                  <select name="country" value={formData.country} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    {COUNTRIES.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-green-700 font-medium">{t('signup.freeTrial')}</p>
                </div>

                <Button type="submit" disabled={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3">
                  {isLoading ? loadingMsg || t('signup.creating') : t('signup.createAccount')}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button onClick={() => window.location.href = 'https://cleandysoft.com'}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                  {t('signup.haveAccount')}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'verify-email' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <img src="/logo.jpg" alt="Cleandy" className="h-16 mx-auto object-contain mb-6" />
                <h1 className="text-3xl font-bold text-gray-800 mb-2">📧 Email Doğrulama</h1>
                <p className="text-gray-600">
                  <strong>{formData.ownerEmail}</strong> adresine gönderilen 6 haneli kodu giriniz
                </p>
                {countdown > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    ⏰ Kodun süresi: {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                  </p>
                )}
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">6 Haneli Doğrulama Kodu</label>
                  <input type="text" value={verificationCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(val);
                      setError('');
                    }}
                    placeholder="000000"
                    className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={6} autoFocus />
                </div>

                <Button onClick={verifyCode} disabled={isLoading || verificationCode.length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3">
                  {isLoading ? loadingMsg || 'Doğrulanıyor...' : 'Kodu Doğrula ve Hesap Oluştur'}
                </Button>

                <div className="text-center">
                  <button onClick={() => { setStep('details'); setError(''); }}
                    className="text-blue-600 hover:text-blue-700 text-sm">
                    ← Geri Dön
                  </button>
                </div>

                {countdown === 0 && (
                  <div className="text-center pt-4 border-t">
                    <p className="text-sm text-gray-600 mb-2">Kodun süresi doldu mu?</p>
                    <button onClick={sendVerificationCode} disabled={isLoading}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm">
                      Yeni kod gönder
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { SignupPageComponent as SignupPage };
